import React, { useState, useEffect } from 'react';
import { Link2, Target, Globe, Smartphone, Clock, ChartBar, ArrowRight, X } from 'lucide-react';

interface Feature {
  id: string;
  icon: React.ElementType;
  title: string;
  description: string;
  details: {
    headline: string;
    bullets: string[];
    example: {
      scenario: string;
      result: string;
    };
  };
}

const features: Feature[] = [
  {
    id: 'dynamic-routing',
    icon: Link2,
    title: 'Dynamic Routing',
    description: 'Update QR destinations anytime without reprinting.',
    details: {
      headline: 'Change destinations on the fly',
      bullets: [
        'Update URLs instantly without reprinting QR codes',
        'Schedule future URL changes in advance',
        'A/B test different landing pages automatically',
        'Roll back to previous URLs if needed',
      ],
      example: {
        scenario: 'Running a limited-time promotion?',
        result: 'Switch your QR destination to the promo page, then back to normal when it ends.',
      },
    },
  },
  {
    id: 'ab-testing',
    icon: Target,
    title: 'A/B Testing',
    description: 'Split traffic across multiple URLs.',
    details: {
      headline: 'Optimize with data-driven decisions',
      bullets: [
        'Split traffic 50/50, 70/30, or any ratio',
        'Track conversion rates for each variant',
        'Automatic statistical significance detection',
        'Gradually roll out winning variants',
      ],
      example: {
        scenario: 'Testing two landing page designs?',
        result: 'Send 50% to each and see which converts better in real-time.',
      },
    },
  },
  {
    id: 'location-routing',
    icon: Globe,
    title: 'Geo Routing',
    description: 'Serve content based on user location.',
    details: {
      headline: 'Deliver localized experiences globally',
      bullets: [
        'Route by country, region, or city',
        'Serve language-specific landing pages',
        'Comply with regional regulations',
        'Show location-relevant promotions',
      ],
      example: {
        scenario: 'Global product launch?',
        result: 'EU users see EU pricing, US users see USD, Asian users get localized content.',
      },
    },
  },
  {
    id: 'device-detection',
    icon: Smartphone,
    title: 'Device Detection',
    description: 'Optimize for mobile, tablet, or desktop.',
    details: {
      headline: 'Perfect experience on every device',
      bullets: [
        'iOS to App Store, Android to Play Store',
        'Mobile-optimized vs desktop experiences',
        'Detect browsers for compatibility routing',
        'Tablet-specific layouts when needed',
      ],
      example: {
        scenario: 'Promoting your mobile app?',
        result: 'One QR automatically sends each user to their correct app store.',
      },
    },
  },
  {
    id: 'time-scheduling',
    icon: Clock,
    title: 'Time Scheduling',
    description: 'Schedule URLs for events and campaigns.',
    details: {
      headline: 'Automate your marketing timeline',
      bullets: [
        'Set different destinations by time of day',
        'Schedule campaign start and end dates',
        'Timezone-aware routing',
        'Automatic expiration handling',
      ],
      example: {
        scenario: 'Restaurant QR code?',
        result: 'Morning shows breakfast, lunch shows specials, evening shows dinner menu.',
      },
    },
  },
  {
    id: 'analytics',
    icon: ChartBar,
    title: 'Analytics',
    description: 'Track scans, devices, and conversions.',
    details: {
      headline: 'Deep insights into every scan',
      bullets: [
        'Real-time scan tracking and notifications',
        'Geographic heat maps of scan activity',
        'Device and browser breakdown charts',
        'Export data for custom analysis',
      ],
      example: {
        scenario: 'Want to know your best-performing locations?',
        result: 'See which physical placements drive the most scans and conversions.',
      },
    },
  },
];

export function Features() {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const selectedFeature = features.find((f) => f.id === selectedId);

  // Read URL hash on mount and when it changes
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.slice(1); // Remove the # symbol
      if (hash) {
        // Check if hash matches any feature ID
        const featureExists = features.some((f) => f.id === hash);
        if (featureExists) {
          setSelectedId(hash);
          // Scroll to features section if not already visible
          const featuresSection = document.querySelector('[data-section="features"]');
          if (featuresSection) {
            featuresSection.scrollIntoView({ behavior: 'smooth' });
          }
        }
      }
    };

    // Check hash on mount
    handleHashChange();

    // Listen for hash changes
    window.addEventListener('hashchange', handleHashChange);

    return () => {
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, []);

  // Update URL hash when selectedId changes (but don't trigger navigation)
  useEffect(() => {
    if (selectedId) {
      // Update hash without triggering scroll
      const currentHash = window.location.hash.slice(1);
      if (currentHash !== selectedId) {
        window.history.replaceState(null, '', `#${selectedId}`);
      }
    }
  }, [selectedId]);

  return (
    <section className="bg-white min-h-screen flex flex-col justify-center section-padding" data-section="features">
      <div className="container-page">
        {/* Header */}
        <div className="text-center mb-16 transition-all duration-700 ease-in-out">
          <p className="label text-[--clinqr-primary] mb-4 font-bold">FEATURES</p>
          <h2 className="heading-1 mb-4 text-[--clinqr-navy]">
            Everything You Need to Control Your QR Codes
          </h2>
          <p className="body-lg max-w-2xl mx-auto text-[--clinqr-navy]/60 font-medium">
            From simple redirects to complex routing logic—all in one platform.
          </p>
        </div>

        {/* Features Container */}
        <div className="relative">
          {/* Feature Tabs Row - shown when a feature is selected */}
          {selectedId && (
            <div className="flex flex-wrap items-center justify-center gap-2 mb-10 transition-all duration-700 ease-in-out opacity-0 animate-fade-in">
              {features.map((feature) => {
                const Icon = feature.icon;
                const isSelected = feature.id === selectedId;

                return (
                  <button
                    key={feature.id}
                    onClick={() => setSelectedId(feature.id)}
                    className={`flex items-center gap-2 px-5 py-3 rounded-2xl transition-all duration-300 ${
                      isSelected
                        ? 'bg-[--clinqr-navy] text-[--clinqr-lime] shadow-2xl scale-[1.05]'
                        : 'bg-[--clinqr-navy]/5 hover:bg-gray-200 text-[--clinqr-navy]/60 hover:text-[--clinqr-navy] border border-[--clinqr-navy]/5'
                    }`}
                  >
                    <Icon className={`w-4 h-4 ${isSelected ? 'text-[--clinqr-lime]' : 'text-[--clinqr-navy]/40'} transition-colors`} />
                    <span className="text-sm font-bold">{feature.title}</span>
                  </button>
                );
              })}
            </div>
          )}

          {/* Expanded Feature Card */}
          {selectedFeature && (
            <div className="bg-[#F1F4F3] rounded-3xl p-8 sm:p-12 border border-[--clinqr-navy]/5 mb-8 transition-all duration-700 ease-in-out opacity-0 animate-fade-in shadow-xl">
              <div className="flex items-start justify-between mb-10 transition-all duration-700 ease-in-out">
                <div className="flex items-center gap-6">
                  <div className="w-16 h-16 rounded-2xl bg-[--clinqr-navy] flex items-center justify-center shadow-lg">
                    <selectedFeature.icon className="w-8 h-8 text-[--clinqr-lime]" strokeWidth={1.5} />
                  </div>
                  <div>
                    <h3 className="heading-3 text-[--clinqr-navy]">{selectedFeature.title}</h3>
                    <p className="text-[--clinqr-navy]/40 font-bold uppercase tracking-widest text-xs mt-1">{selectedFeature.details.headline}</p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setSelectedId(null);
                    // Clear URL hash when closing
                    window.history.replaceState(null, '', window.location.pathname);
                  }}
                  className="w-12 h-12 rounded-full bg-[--clinqr-navy]/5 hover:bg-gray-200 flex items-center justify-center transition-all"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="grid md:grid-cols-2 gap-12 transition-all duration-700 ease-in-out">
                {/* Bullets */}
                <ul className="space-y-5">
                  {selectedFeature.details.bullets.map((bullet, index) => (
                    <li key={index} className="flex items-start gap-4">
                      <div className="w-6 h-6 rounded-full bg-[--clinqr-navy] flex items-center justify-center flex-shrink-0 mt-0.5 shadow-md">
                        <svg className="w-3.5 h-3.5 text-[--clinqr-lime]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={4} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <span className="text-[--clinqr-navy]/70 text-lg font-medium leading-relaxed">{bullet}</span>
                    </li>
                  ))}
                </ul>

                {/* Example */}
                <div className="bg-[--clinqr-navy] rounded-3xl p-8 shadow-2xl flex flex-col justify-center">
                  <p className="text-xs text-[--clinqr-lime] font-bold uppercase tracking-[0.2em] mb-4">Real-world Example</p>
                  <p className="text-xl font-bold text-white mb-4 leading-tight">{selectedFeature.details.example.scenario}</p>
                  <p className="text-[--clinqr-lime] text-lg font-medium leading-relaxed italic">"{selectedFeature.details.example.result}"</p>
                </div>
              </div>
            </div>
          )}

          {/* Feature Cards Grid - shown when no feature is selected */}
          {!selectedId && (
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 transition-all duration-500 ease-in-out opacity-0 animate-fade-in">
              {features.map((feature) => {
                const Icon = feature.icon;

                return (
                  <button
                    key={feature.id}
                    onClick={() => setSelectedId(feature.id)}
                    className="group text-left bg-[#F1F4F3]/50 rounded-3xl p-8 border border-[--clinqr-navy]/5 hover:border-[--clinqr-navy]/20 shadow-xl hover:shadow-3xl transition-all duration-500 hover:-translate-y-2"
                  >
                    <div className="w-14 h-14 rounded-2xl bg-[--clinqr-navy] flex items-center justify-center mb-6 group-hover:scale-110 group-hover:rotate-3 transition-all duration-500 shadow-lg">
                      <Icon className="w-7 h-7 text-[--clinqr-lime]" strokeWidth={1.5} />
                    </div>

                    <h3 className="heading-4 text-[--clinqr-navy] mb-3 group-hover:text-[--clinqr-primary] transition-colors">
                      {feature.title}
                    </h3>

                    <p className="text-sm text-[--clinqr-navy]/60 mb-6 font-medium leading-relaxed">
                      {feature.description}
                    </p>

                    <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-[--clinqr-navy] opacity-0 group-hover:opacity-100 transition-all translate-x-[-10px] group-hover:translate-x-0">
                      Explore
                      <ArrowRight className="w-4 h-4" />
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Hint text */}
        {!selectedId && (
          <p className="text-center text-sm text-[--clinqr-navy]/30 mt-12 font-bold uppercase tracking-widest transition-all duration-500 ease-in-out">
            Click any feature to learn more
          </p>
        )}
      </div>

      <style>{`
        @keyframes fade-in {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animate-fade-in {
          animation: fade-in 0.5s ease-out forwards;
        }
      `}</style>

    </section>
  );
}
